import i18n from "i18n";
import { formatPeriodMonth } from "features/billing-period/billing-period";

/** `t` from useTranslation, typed loosely as the other error helpers do. */
type TFunction = (key: string, opts?: any) => string;

type ErrorEntry = {
  /** i18n key */
  key: string;
  /** English fallback shown when the key is missing */
  def: string;
};

/**
 * Error codes the sales-invoice endpoints answer with — from erp-backend's
 * `services/invoices/generate-from-order.js` and the controllers built on it
 * (`orders/create-invoice`, `invoices/confirm`, `invoices/send`,
 * `invoices/patch`, `invoices/delete-draft`,
 * `customers/create-invoices-bulk`), plus the
 * billing-period/invoice-series services those throw through. Most of these
 * reach the browser as the server's own English sentence today — mapped here
 * so a code we DO recognise reads in the user's language instead of that raw
 * text, the way `orders-settings-errors.ts` and `external-contract-errors.ts`
 * already do for their own endpoints.
 *
 * `PERIOD_ALREADY_BILLED` and `INVOICE_DATE_BEFORE_LAST_ISSUED` are handled
 * specially below, ahead of this table, because their message needs to name
 * the clashing invoice or period the server sent alongside the code.
 */
const CODE_ENTRIES: Record<string, ErrorEntry> = {
  NO_ITEMS: {
    key: "invoice_err_no_items",
    def: "The invoice must have at least one line.",
  },
  NOTHING_TO_BILL: {
    key: "invoice_err_nothing_to_bill",
    def: "Nothing to invoice: every month up to now is invoiced or was skipped.",
  },
  INVALID_AMOUNT: {
    key: "invoice_err_invalid_amount",
    def: "One of the line amounts is invalid.",
  },
  INVALID_LINE: {
    key: "invoice_err_invalid_line",
    def: "One of the invoice lines is invalid — check for a duplicate line, or a title/description that is too long.",
  },
  INVALID_SERVICE_ACT: {
    key: "invoice_err_invalid_service_act",
    def: "The service completion act text is not valid — it can be at most 20,000 characters.",
  },
  INVALID_DATE: {
    key: "invoice_err_invalid_date",
    def: "One of the dates is not valid. Check the invoice date and payment due date.",
  },
  NO_VAT: {
    key: "invoice_err_no_vat",
    def: "No VAT rate is available for this product.",
  },
  PRODUCT_VAT_MISSING: {
    key: "invoice_err_no_vat",
    def: "No VAT rate is available for this product.",
  },
  PRODUCT_VAT_FOREIGN: {
    key: "invoice_err_vat_foreign",
    def: "That VAT rate belongs to another company.",
  },
  NO_PRODUCT: {
    key: "invoice_err_no_product",
    def: "Product not found for this company.",
  },
  ORDER_NOT_SIGNED: {
    key: "invoice_err_order_not_signed",
    def: "The order must be fully signed before it can be invoiced.",
  },
  ORDER_CLOSED: {
    key: "invoice_err_order_closed",
    def: "This order is closed, so it can no longer be invoiced.",
  },
  ORDER_CONTRACT_ENDED: {
    key: "invoice_err_order_contract_ended",
    def: "The order's contract period has ended, so it can no longer be invoiced.",
  },
  ORDER_CONTRACT_ANNULLED: {
    key: "invoice_err_order_contract_annulled",
    def: "This order's contract has been annulled, so it can no longer be invoiced.",
  },
  ORDER_BILLING_CHANGED: {
    key: "invoice_err_order_billing_changed",
    def: "This order's billing settings changed — reopen it to see the current period.",
  },
  ORDER_NOT_FOUND: {
    key: "invoice_err_order_not_found",
    def: "Order not found.",
  },
  CUSTOMER_NOT_FOUND: {
    key: "invoice_err_customer_not_found",
    def: "Customer not found.",
  },
  ALREADY_EXISTS: {
    key: "invoice_already_exists",
    def: "Invoice already exists for this order",
  },
  INVOICE_NOT_FOUND: {
    key: "invoice_err_invoice_not_found",
    def: "Invoice not found.",
  },
  INVOICE_ACCESS_DENIED: {
    key: "invoice_err_access_denied",
    def: "You do not have access to this order's customer.",
  },
  NOT_DRAFT: {
    key: "invoice_err_not_draft",
    def: "Only draft invoices can be edited or deleted.",
  },
  NOT_ERP: {
    key: "invoice_err_not_erp",
    def: "Only ERP invoices can be confirmed.",
  },
  INVOICE_NOT_CONFIRMED: {
    key: "invoice_err_not_confirmed",
    def: "Only confirmed invoices can be emailed to the customer.",
  },
  ALREADY_SENT: {
    key: "invoice_err_already_sent",
    def: "This invoice was already emailed to the customer.",
  },
  missing_invoice_email: {
    key: "invoice_err_missing_email",
    def: "The customer has no invoice email. Enter a recipient address.",
  },
  invalid_email: {
    key: "invoice_err_invalid_email",
    def: "The email address is not valid.",
  },
  POPUP_BLOCKED: {
    key: "invoice_err_popup_blocked",
    def: "Allow pop-ups to preview the PDF.",
  },
  NUMBER_LOCK_TIMEOUT: {
    key: "invoice_err_lock_timeout",
    def: "Another confirmation is in progress for this invoice series — try again shortly.",
  },
  CONFIRM_LOCK_TIMEOUT: {
    key: "invoice_err_lock_timeout",
    def: "Another confirmation is in progress for this invoice — try again shortly.",
  },
  SERIES_CHANGED: {
    key: "invoice_err_series_changed",
    def: "The company's active invoice series changed — try again.",
  },
  SERIES_NOT_FOUND: {
    key: "invoice_err_series_not_configured",
    def: "No invoice numbering series is configured for this company. Set one up in Orders settings.",
  },
  SERIES_SHARED_WITH_FINVALDA: {
    key: "invoice_err_series_shared_finvalda",
    def: "This numbering series is also used by imported Finvalda invoices — choose a series dedicated to ERP invoices.",
  },
  SERIES_NOT_CONFIGURED: {
    key: "invoice_err_series_not_configured",
    def: "No invoice numbering series is configured for this company. Set one up in Orders settings.",
  },
  INVOICE_SERIES_NOT_CONFIGURED: {
    key: "invoice_err_series_not_configured",
    def: "No invoice numbering series is configured for this company. Set one up in Orders settings.",
  },
  INVOICE_SERIES_NOT_FOUND: {
    key: "invoice_err_series_not_configured",
    def: "The configured invoice numbering series no longer exists. Choose one again in Orders settings.",
  },
  INVALID_NUMBER_TEMPLATE: {
    key: "invoice_err_invalid_number_template",
    def: "The invoice numbering series has an invalid number format.",
  },
  INVOICE_DATE_IN_FUTURE: {
    key: "invoice_err_date_in_future",
    def: "The invoice date is later than today. Change the date, or wait until then.",
  },
  INVOICE_NUMBER_TAKEN: {
    key: "invoice_err_number_taken",
    def: "This number already exists on another invoice of the company. Check the series.",
  },
  INVALID_PERIOD: {
    key: "invoice_err_invalid_period",
    def: "That billing period is not valid for this invoice.",
  },
  PERIOD_NOT_ALLOWED: {
    key: "invoice_err_period_not_allowed",
    def: "This invoice's order does not bill by period.",
  },
  PERIOD_NOT_YET_BILLABLE: {
    key: "invoice_err_period_not_yet_billable",
    def: "That month cannot be billed yet.",
  },
  PERIOD_BEFORE_BILLING_START: {
    key: "invoice_err_period_before_billing_start",
    def: "That period is before the order's billing start.",
  },
};

/**
 * The user-facing message for a failed sales-invoice request. Accepts either
 * an Axios error OR a plain `{code, message}` result row — such as one of
 * `createInvoicesBulk`'s per-order failures, which never went through Axios
 * at all — so invoice-form, the drafts panel and the create-invoice modal can
 * all call this the same way.
 */
export function invoiceErrorMessage(error: any, t: TFunction): string {
  const data = error?.response?.data ?? error ?? {};
  const code: string | undefined = data?.code;

  if (code === "PERIOD_ALREADY_BILLED") {
    // Name the invoice already covering the month, so it can be found.
    const which = data.invoiceNumber
      ? data.invoiceNumber
      : data.invoiceStatus === "draft"
      ? t("draft", { defaultValue: "Draft" })
      : "";
    return which
      ? t("invoice_period_already_billed_by", {
          month: formatPeriodMonth(data.periodFrom),
          invoice: which,
        })
      : t("invoice_period_already_billed", {
          defaultValue:
            "That month is already billed by another invoice of this order.",
        });
  }
  if (code === "INVOICE_DATE_BEFORE_LAST_ISSUED") {
    if (data.latestIssuedPeriod) {
      return t("invoice_err_date_before_last_issued", {
        period: data.latestIssuedPeriod,
        defaultValue:
          "This series has already issued invoices in {{period}}, so this date would break its sequence. Change the invoice date, or use a series whose number carries no date.",
      });
    }
    // A DIFFERENT key, not the same one with an empty `period` — a real
    // i18next table already has `invoice_err_date_before_last_issued`
    // translated with a "{{period}}" in it, and would use that translation
    // over `defaultValue` regardless, printing "...issued in , so..." with
    // nothing between "in" and the comma.
    return t("invoice_err_date_before_last_issued_unknown_period", {
      defaultValue:
        "This date would break the series' number sequence. Change the invoice date, or use a series whose number carries no date.",
    });
  }

  if (code && CODE_ENTRIES[code]) {
    const entry = CODE_ENTRIES[code];
    return t(entry.key, { defaultValue: entry.def });
  }

  // Not one of the codes above — most refusals reach here. The server sends
  // its own `message`, in English; some server messages are themselves
  // i18n keys (the "access_denied" style convention other screens rely on),
  // so try that before showing it verbatim. Never handed to `t()` twice: a
  // message containing ":" would otherwise be misread as an i18next
  // namespace separator.
  const message = data?.message;
  if (message) {
    // `as any`: src/i18n.js re-exports the i18next instance under a name that
    // collides with baseUrl-relative resolution (see billing-period.ts, same
    // cast for the same reason) — its inferred type is unreliable here.
    return (i18n as any).exists(message) ? t(message) : message;
  }
  if (typeof error?.message === "string" && error.message) return error.message;

  const status = error?.response?.status;
  if (status === 403) return t("forbidden", { defaultValue: "Forbidden" });
  if (status === 404)
    return t("invoice_err_invoice_not_found", { defaultValue: "Invoice not found." });
  return t("error", { defaultValue: "Error" });
}

/**
 * What to say after a `POST /invoices/:id/confirm` that did not throw — the
 * invoice IS numbered either way; this is only about the PDF/email step.
 * Shared by invoice-form.tsx (single confirm) and the drafts panel (bulk
 * confirm), so the wording for a given `emailSkipReason` never drifts between
 * the two.
 *
 * `emailSkipReason` values are `resolveConfirmPostAction`'s and
 * `confirmInvoice`'s (generate-from-order.js): `not_requested` (the user did
 * not tick "email the customer" — not a failure), `missing_invoice_email`
 * (customer has none — not a failure), `invalid_email` (address does not
 * parse — needs a customer fix), `pdf_failed`, `send_failed` (the provider
 * rejected the message — the invoice IS issued and can be sent again from the
 * confirmed list) and `already_confirmed`.
 */
export function invoiceEmailSkipMessage(
  t: TFunction,
  skipReason: string | null | undefined,
  emailSent: boolean,
  emailError?: string | null
): { ok: boolean; message: string } {
  if (emailSent) {
    return {
      ok: true,
      message: t("invoice_confirmed_emailed", {
        defaultValue: "Confirmed and emailed",
      }),
    };
  }
  if (skipReason === "not_requested") {
    return {
      ok: true,
      message: t("invoice_confirmed_not_emailed", {
        defaultValue:
          "Confirmed. It was not emailed — send it from Outgoing invoices.",
      }),
    };
  }
  if (skipReason === "missing_invoice_email") {
    return {
      ok: true,
      message: t("invoice_email_skipped_no_address", {
        defaultValue: "Confirmed — no invoice email",
      }),
    };
  }
  if (skipReason === "invalid_email") {
    return {
      ok: false,
      message: emailError
        ? t("invoice_email_skipped_invalid_with_reason", {
            reason: emailError,
            defaultValue:
              "Confirmed, but the invoice email address is invalid — {{reason}}. Fix it on the customer and use Send again.",
          })
        : t("invoice_email_skipped_invalid", {
            defaultValue:
              "Confirmed, but the invoice email address is invalid. Fix it on the customer and use Send again.",
          }),
    };
  }
  if (skipReason === "pdf_failed") {
    return {
      ok: false,
      message: t("invoice_email_skipped_pdf", {
        defaultValue:
          "Confirmed, but PDF generation failed — customer was not emailed.",
      }),
    };
  }
  if (skipReason === "send_failed") {
    return {
      ok: false,
      message: emailError
        ? t("invoice_email_skipped_send_with_reason", {
            reason: emailError,
            defaultValue:
              "Confirmed, but sending the email failed — {{reason}}. Use Send again once fixed.",
          })
        : t("invoice_email_skipped_send", {
            defaultValue:
              "Confirmed, but sending the email failed. Check mail settings / logs.",
          }),
    };
  }
  if (skipReason === "already_confirmed") {
    return {
      ok: true,
      message: t("invoice_already_confirmed", {
        defaultValue: "Already confirmed (email is not sent again).",
      }),
    };
  }
  return {
    ok: true,
    message: t("invoice_confirmed", { defaultValue: "Confirmed" }),
  };
}
